import { UserManager } from 'oidc-client-ts'
import { EXTERNAL_AUTHENTICATION_PROVIDERS } from '~/constants/authentication'

import type { AUTHENTICATION_INTENT } from '~/constants/authentication'

export default function useExternalAuthProvider() {
  const runtimeConfig = useRuntimeConfig()
  const { $captureError, $i18n } = useNuxtApp()

  const { isMobileOrTablet } = useDevice()
  const { routeBaseName } = useBaseName()
  const route = useRoute()
  const { isInCaOrUs, geolocation } = useGeolocation()
  const isExternalAuthenticationLoading = useIsExternalAuthenticationLoading()
  const externalAuthError = useExternalAuthError()

  const providerScopeMap: Record<EXTERNAL_AUTHENTICATION_PROVIDERS, string> = {
    apple: 'openid profile email',
    facebook: 'email',
    google: 'openid profile email',
  }

  // OIDC - Identity Server Auth Config
  function getProviderConfig(provider: EXTERNAL_AUTHENTICATION_PROVIDERS, additionnalExtraQueryParams = {}) {
    if (!provider || !Object.values(EXTERNAL_AUTHENTICATION_PROVIDERS).includes(provider)) {
      throw new Error('Invalid provider')
    }

    const providerName = provider.toString()
    return {
      authority: runtimeConfig.public.oidc.authorityUrl,
      client_id: runtimeConfig.public.oidc.clientId,
      redirect_uri: `${runtimeConfig.public.baseURL}/callback-oauth?action=signin&provider=${providerName}`,
      post_logout_redirect_uri: `${runtimeConfig.public.baseURL}/callback-oauth?action=signout&provider=${providerName}`,
      response_type: 'code',
      scope: providerScopeMap[provider],
      loadUserInfo: true,
      client_secret: runtimeConfig.public.oidc.clientSecret,
      extraQueryParams: {
        asch: providerName,
        ...additionnalExtraQueryParams,
      },
    }
  }

  async function externalDeauthenticate(provider: EXTERNAL_AUTHENTICATION_PROVIDERS) {
    const additionnalExtraQueryParams = {
      lang: $i18n.locale.value,
    }

    try {
      const providerConfig = getProviderConfig(provider, additionnalExtraQueryParams)

      const userManager = new UserManager(providerConfig)

      await userManager.signoutSilent()
    }
    catch (err: unknown) {
      $captureError(err)
    }
  }

  async function triggerExternalAuth({
    provider,
    isSignupEvent = false,
    intent,
    redirectPath,
  }: {
    provider: EXTERNAL_AUTHENTICATION_PROVIDERS
    isSignupEvent: boolean
    intent?: AUTHENTICATION_INTENT
    redirectPath?: string
  }) {
    if (!provider) {
      return
    }

    const isOwnerSignup = intent === 'ListingIntent'

    try {
      isExternalAuthenticationLoading.value = true

      const recaptchaToken = await getRecaptchaToken() ?? ''

      // Augment query params with additional data
      const additionnalExtraQueryParams = {
        lang: $i18n.locale.value,
        ownerNewsletter: isOwnerSignup,
        renterNewsletter: !isOwnerSignup,
        signUpAs: isOwnerSignup ? 1 : 2,
        signUpCountry: (isInCaOrUs && geolocation.value?.countryCode) ?? '',
        isSignUp: isSignupEvent,
        platform: isMobileOrTablet ? 'mobile_web' : 'web',
        pageSource: routeBaseName.value,
        hearAboutUs: route.query.hearAboutUs ?? null,
        referralCode: route.query.referralCode ?? null,
        recaptchaToken,
      }

      const providerConfig = getProviderConfig(provider, additionnalExtraQueryParams)

      const userManager = new UserManager(providerConfig)

      const intentCookie = useIntentCookie()
      const authTypeCookie = useAuthTypeCookie()
      const redirectCookie = useRedirectCookie()

      intentCookie.value = intent?.toString() ?? null
      authTypeCookie.value = isSignupEvent ? 'Signup' : 'Login'
      redirectCookie.value = redirectPath ?? redirectCookie.value ?? route.fullPath

      await userManager.signinRedirect()
    }
    catch (err: unknown) {
      isExternalAuthenticationLoading.value = false
      $captureError(err)
      const code = 'EXTERNAL_AUTH_FAILED'
      externalAuthError.value = { code }
    }
  }

  async function getRecaptchaToken(): Promise<string> {
    if (!runtimeConfig.public.recaptcha.enabled) {
      return ''
    }

    try {
      const recaptcha = await useRecaptcha()
      const token = await recaptcha?.execute('SOCIALSIGNUP')

      return token ?? ''
    }
    catch (_error) {
      // Silently fail. We let the back-end handle the error of a missing token.
      return ''
    }
  }

  return {
    getProviderConfig,
    externalDeauthenticate,
    triggerExternalAuth,
  }
}
